/**
 * Genera una función key para useSWRInfinite que maneja la paginación basada en arrays.
 * La función retorna null cuando no hay más páginas que cargar (cuando la página anterior está vacía).
 *
 * @param swrKey - Key base de SWR (puede ser null o un array de valores)
 * @returns Función que puede ser usada como primer parámetro de useSWRInfinite
 *
 * @example
 * const swrKey = useMemo(() => ['orders', statusId, queryParams] : null, [statusId, queryParams]);
 * const getKey = useGetSWRInfiniteKey(swrKey);
 * const { data } = useSWRInfinite(getKey, fetcher);
 */

type TPageWithLength = { length: number };

export const useGetSWRInfiniteKey = (swrKey: (string | number)[] | null) => {
  return (pageIndex: number, previousPageData: TPageWithLength | null): (string | number)[] | null => {
    if (!swrKey) return null;
    if (previousPageData && previousPageData?.length === 0) return null;
    return [...swrKey, pageIndex];
  };
};

/**
 * Verifica si hay más páginas disponibles en una paginación infinita.
 * Determina si hay más datos basándose en si la última página tiene el tamaño completo esperado.
 *
 * @param pages - Array de páginas (array de arrays) obtenido de useSWRInfinite
 * @param pageSize - Tamaño esperado de una página completa (por defecto 10)
 * @returns true si hay más páginas disponibles, false en caso contrario
 *
 * @example
 * const { data: pages } = useSWRInfinite(getKey, fetcher);
 * const hasMore = hasMorePages(pages, 10);
 */
export const hasMorePages = (pages: TPageWithLength[] | undefined, pageSize: number = 10): boolean => {
  if (!pages?.length) return false;
  const lastPage = pages?.[pages?.length - 1];
  return lastPage != null && lastPage?.length === pageSize;
};
